import type { AxiosError } from "axios";
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { env } from "../env";
import { IDEMPOTENCY_HEADER, UNSAFE_METHODS, newIdempotencyKey } from "./idempotency";
import { useAuthStore } from "@/features/auth/stores/authStore";
import type { DeveloperUser } from "@/features/auth/api/types";

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
  // Envia o cookie httpOnly do refresh token nas chamadas a /auth/refresh.
  withCredentials: true,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  const lang = document.documentElement.lang || "pt-BR";
  config.headers.set("Accept-Language", lang);

  // Toda escrita leva Idempotency-Key: a API exige o header e usa a chave para
  // deduplicar duplo-clique e retentativa. Só carimba se ainda não houver chave —
  // assim a repetição pós-refresh de token (abaixo) reaproveita a MESMA chave, que é
  // a mesma intenção do usuário, e quem precisa fixar a chave pode mandá-la no config.
  if (UNSAFE_METHODS.has((config.method ?? "get").toLowerCase()) && !config.headers.has(IDEMPOTENCY_HEADER)) {
    config.headers.set(IDEMPOTENCY_HEADER, newIdempotencyKey());
  }

  return config;
});

/** Resposta de /auth/refresh (mesmo shape do login). */
interface RefreshResponse {
  accessToken: string;
  user: Omit<DeveloperUser, "authProvider">;
}

// Single-flight: várias requests que tomam 401 ao mesmo tempo compartilham
// um único refresh em andamento.
let refreshing: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      // axios "cru" (sem os interceptors) para não recursar no 401.
      const { data } = await axios.post<RefreshResponse>(
        `${env.VITE_API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      const current = useAuthStore.getState();
      current.setSession({
        accessToken: data.accessToken,
        user: { ...data.user, authProvider: current.user?.authProvider ?? "local" },
      });
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const isAuthCall = (original?.url ?? "").includes("/auth/");

    if (status !== 401 || !original || isAuthCall) {
      return Promise.reject(error);
    }

    // 1ª falha 401 numa request normal → tenta refresh uma vez e repete.
    if (!original._retried) {
      original._retried = true;
      if (await refreshSession()) {
        const token = useAuthStore.getState().accessToken;
        if (token) original.headers.set("Authorization", `Bearer ${token}`);
        return apiClient(original);
      }
    }

    useAuthStore.getState().logout();
    return Promise.reject(error);
  },
);
